import React from "react";
// Events Page

// Default
import { useEffect, useState } from "react";

// Mui
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Popover from "@mui/material/Popover";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";

// Custom
import Layout from "../../layout";
import CustomSearch from "../../components/search/Search";
import CustomDataTable from "../../components/table/CustomTable";
import DateAndTimePopover from "../../components/popovers/DateAndTimePopover";
import CustomToast from "../../components/toast/CustomToast";

// Svg
import Download from "../../assets/svg/download";
import Calendar2 from "../../assets/svg/calendar2";

// Utils
import { fetchGETRequest } from "../../utils/services";
import { formatTime, getTimeStamp, formatDate } from "../../utils/helper";
import { ToastState } from "../../types";

// Types
interface EventData {
  eventDate: string;
  meterId: string;
  eventCode: string;
  eventDescription: string;
}

interface TimeStamp {
  start: number;
  end: number;
}

interface ApiResponse {
  EventCode?: Array<{ ts: number; value: string }>;
  EventDescription?: Array<{ ts: number; value: string }>;
}

const Events: React.FC = () => {
  const currentTimestamp = Date.now();
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [searchValue, setSearchValue] = useState<string>("");
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [datePickerEl, setDatePickerEl] = useState<HTMLElement | null>(null);
  const [timeRange, setTimeRange] = useState<number[]>([0, 1439]);
  const [timeStamp, setTimeStamp] = useState<TimeStamp>({
    start: currentTimestamp - 12 * 60 * 60 * 1000,
    end: currentTimestamp,
  });
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [eventsData, setEventsData] = useState<EventData[]>([]);
  const [toast, setToast] = useState<ToastState>({
    message: "",
    severity: "",
  });

  const handleDateChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    if (start) setStartDate(start);
    if (end) setEndDate(end);
  };

  const COLUMNS = ["Event Date", "Meter ID", "Event Code", "Event Description"];

  const handleDateSubmit = () => {
    const startTs = getTimeStamp(formatTime(timeRange[0]), new Date(startDate));
    const endTs = getTimeStamp(formatTime(timeRange[1]), new Date(endDate));

    setTimeStamp({
      start: startTs,
      end: endTs,
    });

    setDatePickerEl(null);
  };

  const handleTimeRangeChange = (timeRange: number | number[]) => {
    if (Array.isArray(timeRange)) {
      setTimeRange(timeRange);
    } else {
      setTimeRange([timeRange, timeRange]);
    }
  };

  const rowsContent = eventsData.map((item: EventData, index: number) => (
    <TableRow
      key={index}
      sx={{
        height: "50px",
        background: (index + 1) % 2 ? "#F9FCFE" : "transparent",
      }}
      className="custom_table_tbody">
      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item?.eventDate}
        </Typography>
      </TableCell>

      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item?.meterId}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item?.eventCode}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item?.eventDescription}
        </Typography>
      </TableCell>
    </TableRow>
  ));

  const getAPIData = async () => {
    if (!searchValue) {
      setToast({
        message: "Please Enter the meter.",
        severity: "error",
      });
      return;
    }
    const response: any = await fetchGETRequest(
      `plugins/telemetry/DEVICE/${searchValue}/values/timeseries?keys=EventCode,EventDescription&startTs=${timeStamp?.start}&endTs=${timeStamp?.end}&limit=${rowsPerPage}`
    );

    const responseData = response?.EventCode?.map(
      (item: any, index: number) => {
        return {
          eventDate: formatDate(new Date(item.ts), "DD/MM/YYYY HH:mm:ss"),
          meterId: searchValue,
          eventCode: item.value,
          eventDescription: response?.EventDescription?.[index]?.value || "",
        };
      }
    );

    if (responseData) {
      setEventsData([...responseData]);
    }
  };

  useEffect(() => {
    if (searchValue) {
      getAPIData();
    }
  }, [rowsPerPage, timeStamp]);

  return (
    <Layout>
      <CustomToast
        open={toast?.message}
        severity={toast?.severity || "info"}
        setToast={setToast}
      />
      <Card>
        <CardContent>
          <Stack
            direction={"row"}
            justifyContent={"flex-end"}
            alignItems={"center"}
            gap={"20px"}>
            <CustomSearch
              value={searchValue}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSearchValue(e.target.value)
              }
              sx={{
                height: "40px",
                width: "400px",
              }}
              placeholder={"Search Meters..."}
              onClick={getAPIData}
            />
            <IconButton
              onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
                setDatePickerEl(event.currentTarget)
              }>
              <Calendar2 />
            </IconButton>
            <IconButton
              onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
                setAnchorEl(event.currentTarget)
              }>
              <Download />
            </IconButton>

            <Popover
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}>
              <IconButton>
                <Typography sx={{ p: 2 }}>Csv</Typography>
              </IconButton>
              <Divider />
              <IconButton>
                <Typography sx={{ p: 2 }}>Excel</Typography>
              </IconButton>
            </Popover>
          </Stack>
        </CardContent>
      </Card>

      <DateAndTimePopover
        anchorEl={datePickerEl}
        setAnchorEl={setDatePickerEl}
        startDate={startDate}
        endDate={endDate}
        handleDateChange={handleDateChange}
        timeRange={timeRange}
        setTimeRange={handleTimeRangeChange}
        handleDateSubmit={handleDateSubmit}
      />

      <Card sx={{ mt: "20px" }}>
        <CardHeader title={<Typography variant="body1">Events</Typography>} />
        <Divider />
        <CardContent>
          <CustomDataTable
            sx={{ maxHeight: "600px" }}
            columns={COLUMNS}
            // rows={filteredWorkOrders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)}
            rowsContent={rowsContent}
            // totalCount={filteredWorkOrders?.length}
            pagination={true}
            Actions={false}
            rowsPerPage={rowsPerPage}
            page={page}
            onRowsPerPageChange={(
              newRowsPerPage: React.ChangeEvent<
                HTMLInputElement | HTMLTextAreaElement
              >
            ) => {
              setRowsPerPage(parseInt(newRowsPerPage.target.value, 10));
              setPage(0);
            }}
          />
        </CardContent>
      </Card>
    </Layout>
  );
};

export default Events;
